import { companyAPI } from './api/companyAPI.js';

class CompanyService {
  constructor() {
    // Cache company data to avoid repeated API calls
    this.companyCache = new Map();
  }

  /**
   * Get company name from company code, with caching
   * @param {string} companyCode - The company code
   * @returns {Promise<string>} The company name
   */
  async getCompanyName(companyCode) {
    try {
      if (!companyCode || typeof companyCode !== 'string') {
        return 'Nespecificat';
      }

      // Check cache first
      if (this.companyCache.has(companyCode)) {
        return this.companyCache.get(companyCode);
      }

      // Fetch from API if not in cache
      const companyData = await companyAPI.getByCode(companyCode);
      const companyName = companyData.name || companyCode;

      // Store in cache
      this.companyCache.set(companyCode, companyName);

      return companyName;
    } catch (error) {
      console.warn(`Could not resolve company name for code: ${companyCode}`, error);
      // Return the code as fallback if we can't get the name
      return companyCode || 'Nespecificat';
    }
  }

  /**
   * Get full company data from company code, with caching
   * @param {string} companyCode - The company code
   * @returns {Promise<Object>} The company data object
   */
  async getCompanyData(companyCode) {
    try {
      if (!companyCode || typeof companyCode !== 'string') {
        return { code: '', name: 'Nespecificat', address: '' };
      }

      // Create cache key for full data
      const fullDataKey = `${companyCode}_full`;

      // Check cache first
      if (this.companyCache.has(fullDataKey)) {
        return this.companyCache.get(fullDataKey);
      }

      // Fetch from API if not in cache
      const companyData = await companyAPI.getByCode(companyCode);

      // Store in cache
      this.companyCache.set(fullDataKey, companyData);

      return companyData;
    } catch (error) {
      console.warn(`Could not resolve company data for code: ${companyCode}`, error);
      // Return fallback data
      return {
        code: companyCode || '',
        name: companyCode || 'Nespecificat',
        address: ''
      };
    }
  }

  /**
   * Clear the company cache (useful when data might be stale)
   */
  clearCache() {
    this.companyCache.clear();
  }

  /**
   * Pre-load company data for multiple codes
   * @param {string[]} companyCodes - Array of company codes to preload
   */
  async preloadCompanies(companyCodes) {
    const promises = companyCodes.map(code => this.getCompanyName(code));
    await Promise.all(promises);
  }
}

// Export a singleton instance
export const companyService = new CompanyService();
export default companyService;